import { BACKGROUND_COLOR, JITTER_STRENGTH, STROKE_COLOR, WEDGE_STROKE } from './constants';
import { getLocalAngle, TAU, transformPointForSector } from './geometry';

const drawSmoothPath = (ctx, points) => {
  if (points.length === 0) return;
  if (points.length === 1) {
    const [point] = points;
    ctx.beginPath();
    ctx.arc(point.x, point.y, 1.5, 0, TAU);
    ctx.fill();
    return;
  }

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length; i += 1) {
    const current = points[i];
    const previous = points[i - 1];
    const midX = (current.x + previous.x) / 2;
    const midY = (current.y + previous.y) / 2;

    ctx.quadraticCurveTo(previous.x, previous.y, midX, midY);
  }

  const lastPoint = points[points.length - 1];
  ctx.lineTo(lastPoint.x, lastPoint.y);
  ctx.stroke();
};

export const applyWedgeClip = (ctx, geometry) => {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(geometry.centerX, geometry.centerY);
  ctx.lineTo(
    geometry.centerX + Math.cos(geometry.startAngle) * geometry.radius,
    geometry.centerY + Math.sin(geometry.startAngle) * geometry.radius,
  );
  ctx.arc(
    geometry.centerX,
    geometry.centerY,
    geometry.radius,
    geometry.startAngle,
    geometry.startAngle + geometry.wedgeAngle,
    false,
  );
  ctx.closePath();
  ctx.clip();
};

const drawStrokePoints = (ctx, points, smoothingEnabled, baseWidth) => {
  if (points.length === 0) return;

  ctx.lineWidth = baseWidth;
  if (smoothingEnabled) {
    drawSmoothPath(ctx, points);
    return;
  }

  const [firstPoint, ...nextPoints] = points;
  ctx.beginPath();
  ctx.moveTo(firstPoint.x, firstPoint.y);
  nextPoints.forEach((point) => ctx.lineTo(point.x, point.y));
  ctx.stroke();
};

export const renderWedgeCanvas = ({
  ctx,
  canvasSize,
  geometry,
  strokes,
  currentStroke,
  smoothingEnabled,
}) => {
  ctx.save();
  ctx.clearRect(0, 0, canvasSize, canvasSize);
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  ctx.strokeStyle = 'rgba(12, 40, 58, 0.14)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(geometry.centerX, geometry.centerY, geometry.radius, 0, TAU);
  ctx.stroke();

  ctx.save();
  applyWedgeClip(ctx, geometry);
  ctx.strokeStyle = STROKE_COLOR;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const baseWidth = 2.6;

  strokes.forEach((stroke) => {
    if (!stroke || stroke.points.length < 1) return;
    drawStrokePoints(ctx, stroke.points, smoothingEnabled, baseWidth);
  });

  if (currentStroke?.points?.length) {
    drawStrokePoints(ctx, currentStroke.points, smoothingEnabled, baseWidth);
  }

  ctx.restore();

  ctx.strokeStyle = WEDGE_STROKE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(geometry.centerX, geometry.centerY);
  ctx.lineTo(
    geometry.centerX + Math.cos(geometry.startAngle) * geometry.radius,
    geometry.centerY + Math.sin(geometry.startAngle) * geometry.radius,
  );
  ctx.lineTo(
    geometry.centerX + Math.cos(geometry.startAngle + geometry.wedgeAngle) * geometry.radius,
    geometry.centerY + Math.sin(geometry.startAngle + geometry.wedgeAngle) * geometry.radius,
  );
  ctx.closePath();
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(geometry.centerX, geometry.centerY, 4, 0, TAU);
  ctx.fillStyle = WEDGE_STROKE;
  ctx.fill();

  ctx.restore();
};

export const renderBloomCanvas = ({
  ctx,
  canvasSize,
  geometry,
  folds,
  strokes,
  smoothingEnabled,
  organicMode,
  revealStartAngle,
}) => {
  ctx.save();
  ctx.clearRect(0, 0, canvasSize, canvasSize);
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  ctx.strokeStyle = STROKE_COLOR;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const baseWidth = 2.4;

  const jitter = organicMode ? geometry.radius * JITTER_STRENGTH : 0;
  const revealGeometry =
    typeof revealStartAngle === 'number'
      ? { ...geometry, startAngle: revealStartAngle }
      : geometry;

  const renderStroke = (stroke, sectorIndex, mirror) => {
    const transformedPoints = stroke.points.map((point) => {
      const dx = point.x - geometry.centerX;
      const dy = point.y - geometry.centerY;
      const radius = Math.sqrt(dx * dx + dy * dy);
      const absoluteAngle = Math.atan2(dy, dx);
      const localAngle = getLocalAngle(absoluteAngle, geometry.startAngle);
      const targetPoint = transformPointForSector(
        revealGeometry,
        { radius },
        localAngle,
        mirror,
        sectorIndex,
      );

      const jitterOffset = jitter > 0
        ? {
          x: targetPoint.x + (Math.random() - 0.5) * jitter,
          y: targetPoint.y + (Math.random() - 0.5) * jitter,
        }
        : targetPoint;

      return jitterOffset;
    });

    drawStrokePoints(ctx, transformedPoints, smoothingEnabled, baseWidth);
  };

  for (let sectorIndex = 0; sectorIndex < folds; sectorIndex += 1) {
    const mirror = sectorIndex % 2 === 1;
    strokes.forEach((stroke) => {
      if (!stroke || stroke.points.length === 0) return;
      renderStroke(stroke, sectorIndex, mirror);
    });
  }

  ctx.restore();
};
